import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    AccessRoleDTO,
    useDeleteAccessRoleApi,
    useSaveAccessRoleApi,
    useSearchAccessRoleApi,
} from '../../../api/access-role-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveAccessRoleModal } from './save-access-role-modal';

export function AccessRoleApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedAccessRole, setSelectedAccessRole] = useState<AccessRoleDTO | undefined>();
    const accessRoleInit = useSearchAccessRoleApi();
    const saveAccessRole = useSaveAccessRoleApi();
    const deleteAccessRole = useDeleteAccessRoleApi();

    const { result, error, busy, initialized, initializing } = accessRoleInit;

    const columns: IBasicTableColumn<AccessRoleDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
            },
            {
                Header: <T>title</T>,
                name: 'title',
                width: 80,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        accessRoleInit.post(null, {});
    }, [initialized, initializing, error, accessRoleInit]);

    const handleDelete = (accessRole: AccessRoleDTO) => {
        bd.msgbox(
            translate('delete-access-role'),
            translate('confirm-delete-access-role'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteAccessRole.remove(
                            accessRole,
                            () => {
                                accessRoleInit.updateResult({
                                    ...result,
                                    accessRoles: result.accessRoles.filter(
                                        b => b.id !== accessRole.id
                                    ),
                                });
                                notify.dark(translate('selected-access-role-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedAccessRole(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (accessRole: AccessRoleDTO) => {
        saveAccessRole.post(
            null,
            accessRole,
            res => {
                if (selectedAccessRole) {
                    accessRoleInit.updateResult({
                        ...result,
                        accessRoles: result.accessRoles.map(b =>
                            b.id === accessRole.id ? res.accessRole : b
                        ),
                    });
                } else {
                    accessRoleInit.updateResult({
                        ...result,
                        accessRoles: [res.accessRole, ...result.accessRoles],
                    });
                }

                setShowModal(false);
                setSelectedAccessRole(undefined);
                notify.success(
                    selectedAccessRole
                        ? translate('selected-access-role-updated-successfully')
                        : translate('new-access-role-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => accessRoleInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="access-role"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-access-role</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedAccessRole(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.accessRoles}
                        onRowClick={({ row }) => {
                            setSelectedAccessRole(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveAccessRoleModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedAccessRole(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedAccessRole={selectedAccessRole}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
